import { supabase } from '@/lib/supabase';
import { LinearGradient } from 'expo-linear-gradient';
import { router, Stack, useLocalSearchParams } from 'expo-router';
import { CheckCircle, Crown, Sparkle } from 'phosphor-react-native';
import { useEffect, useRef, useState } from 'react';
import { Image, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 500;

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  purple600: '#7C3AED',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  emerald600: '#059669',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
};

export default function SubscriptionSuccessScreen() {
  const { session_id } = useLocalSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const hasRun = useRef(false); // ✅ Protection contre les re-renders

  console.log('=== SUBSCRIPTION SUCCESS RENDER ===', { session_id, isLoading, hasRun: hasRun.current });

  useEffect(() => {
    // ✅ Ne pas re-exécuter si déjà fait
    if (hasRun.current) {
      console.log('=== useEffect already run, skipping ===');
      return;
    }
    hasRun.current = true;

    console.log('=== SUBSCRIPTION SUCCESS useEffect START ===');

    const verifySubscription = async () => {
      console.log('=== Waiting 3 seconds... ===');
      
      // Attendre que le webhook Stripe traite le paiement
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      console.log('=== Done waiting ===');

      // Vérifier le statut premium directement depuis Supabase
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('premium')
            .eq('id', user.id)
            .single();
          
          console.log('[SubscriptionSuccess] Premium status:', profile?.premium);
          if (profile?.premium) {
            router.replace('/(tabs)/');
            return;
          }  
        }
      } catch (error) {
        console.error('[SubscriptionSuccess] Error checking premium:', error);
      }
      
      console.log('=== Setting isLoading to false ===');
      setIsLoading(false);
    };

    verifySubscription();
  }, []); // ✅ Pas de dépendances - s'exécute une seule fois

  const handleContinue = () => {
    console.log('=== BUTTON CLICKED - navigating to tabs ===');
    router.replace('/(tabs)');
  };

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      
      <View style={styles.container}>
        <LinearGradient
          colors={[colors.indigo100, colors.slate50]}
          style={styles.backgroundGradient}
        />
        
        <View style={styles.content}>
          <View style={styles.innerContainer}>
            {isLoading ? (
              // État de chargement
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.indigo600} />
                <Text style={styles.loadingText}>
                  Activation de ton abonnement...
                </Text>
                <Text style={styles.loadingSubtext}>
                  Cela peut prendre quelques secondes
                </Text>
              </View>
            ) : (
              // État de succès
              <>
                {/* Icône de succès */}
                <View style={styles.successIconContainer}>
                  <LinearGradient
                    colors={[colors.emerald500, colors.emerald600]}
                    style={styles.successIconGradient}
                  >
                    <CheckCircle size={60} color={colors.white} weight="fill" />
                  </LinearGradient>
                </View>

                {/* Mascotte */}
                <View style={styles.mascotContainer}>
                  <Image 
                    source={require('../assets/images/ernest.png')} 
                    style={styles.mascotImage}
                  />
                </View>

                {/* Badge Premium */}
                <View style={styles.premiumBadge}>
                  <Crown size={16} color={colors.amber500} weight="fill" />
                  <Text style={styles.premiumBadgeText}>PREMIUM ACTIVÉ</Text>
                </View>

                {/* Titre */}
                <Text style={styles.title}>
                  Bienvenue dans Ernest Premium ! 🎉
                </Text>

                {/* Sous-titre */}
                <Text style={styles.subtitle}>
                  Tu as maintenant accès à toutes les fonctionnalités pour réussir tes examens.
                </Text>

                {/* Liste des avantages débloqués */}
                <View style={styles.benefitsContainer}>
                  <View style={styles.benefitRow}>
                    <Sparkle size={20} color={colors.indigo600} weight="fill" />
                    <Text style={styles.benefitText}>Fiches, Quiz, Flashcards illimités</Text>
                  </View>
                  <View style={styles.benefitRow}>
                    <Sparkle size={20} color={colors.indigo600} weight="fill" />
                    <Text style={styles.benefitText}>Coach IA disponible 24h/24</Text>
                  </View>
                  <View style={styles.benefitRow}>
                    <Sparkle size={20} color={colors.indigo600} weight="fill" />
                    <Text style={styles.benefitText}>Cours exclusifs par Ernest</Text>
                  </View>
                </View>

                {/* Bouton continuer */}
                <TouchableOpacity
                  style={styles.continueButton}
                  onPress={handleContinue}
                  activeOpacity={0.9}
                >
                  <LinearGradient
                    colors={[colors.indigo600, colors.purple600]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.continueButtonGradient}
                  >
                    <Text style={styles.continueButtonText}>
                      Commencer à réviser
                    </Text>
                  </LinearGradient>
                </TouchableOpacity>

                {/* Message de remerciement */}
                <Text style={styles.thankYouText}>
                  Merci pour ta confiance ! 💜
                </Text>
              </>
            )}
          </View>
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  backgroundGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  innerContainer: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    alignItems: 'center',
  },

  // Loading
  loadingContainer: {
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.slate700,
    textAlign: 'center',
  },
  loadingSubtext: {
    fontSize: 14,
    color: colors.slate500,
    textAlign: 'center',
  },

  // Success Icon
  successIconContainer: {
    marginBottom: 20,
  },
  successIconGradient: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: colors.emerald600,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 8,
  },

  // Mascot
  mascotContainer: {
    marginBottom: 16,
  },
  mascotImage: {
    width: 80,
    height: 80,
    resizeMode: 'contain',
  },

  // Premium Badge
  premiumBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.amber100,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 100,
    marginBottom: 16,
  },
  premiumBadgeText: {
    color: colors.amber500,
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 1,
  },

  // Title
  title: {
    fontSize: 24,
    fontWeight: '800',
    color: colors.slate900,
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: -0.5,
  },

  // Subtitle
  subtitle: {
    fontSize: 15,
    color: colors.slate600,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
    paddingHorizontal: 20,
  },

  // Benefits
  benefitsContainer: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 20,
    width: '100%',
    marginBottom: 24,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
  },
  benefitText: {
    fontSize: 15,
    color: colors.slate700,
    fontWeight: '600',
  },

  // Continue Button
  continueButton: {
    width: '100%',
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.35,
    shadowRadius: 12,
    elevation: 8,
    marginBottom: 16,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  continueButtonGradient: {
    paddingVertical: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  continueButtonText: {
    color: colors.white,
    fontSize: 17,
    fontWeight: '700',
  },

  // Thank you
  thankYouText: {
    fontSize: 14,
    color: colors.slate500,
    textAlign: 'center',
  },
});
